import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, MapPin, Clock } from "lucide-react";
import { BookingModal, openBookingModal } from "@/components/booking-modal";
import { CookieBanner } from "@/components/cookie-banner";
import { LunchBanner } from "@/components/lunch-banner";
import { ExitIntentModal } from "@/components/exit-intent-modal";
import MapPicker from "@/components/map-picker";
import { REVIEW_STATS } from "@/data/review-stats";
import { NewYearTopBanner, useNewYearBannerActive } from "@/components/new-year/new-year-top-banner";

const navLinks = [
  { href: "/menu", label: "Меню" },
  { href: "/interior", label: "Интерьер" },
  { href: "/afisha", label: "Афиша" },
  { href: "/banket", label: "Банкет" },
  { href: "/sertifikat", label: "Сертификаты" },
  { href: "/vakansii", label: "Вакансии" },
  { href: "/contacts", label: "Контакты" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const nyBar = useNewYearBannerActive();

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  return (
    <div className={`min-h-[100dvh] flex flex-col bg-background text-foreground dark relative overflow-x-clip${nyBar ? " ny-bar-on" : ""}`}>
      <CookieBanner />
      <LunchBanner />
      {nyBar && <NewYearTopBanner />}

      {/* Grain texture overlay — purely cosmetic, no pointer events */}
      <div
        className="fixed inset-0 z-50 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "256px 256px",
        }}
      />

      {/*
        Mobile nav overlay — lives at ROOT level (NOT inside <header>) so that
        backdrop-blur on the header cannot act as a containing block for it.
        z-[62]: above lunch banner (z-[59]) but below header (z-[65]).
      */}
      <div
        className={`fixed inset-0 bg-background z-[62] flex flex-col items-center overflow-y-auto pb-8 transition-all duration-500 ${
          mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ paddingTop: `calc(var(--ny-bar-h, 0px) + 128px + ${nyBar ? "0px" : "env(safe-area-inset-top, 0px)"})` }}
      >
        <nav className="flex flex-col items-center gap-4 text-center my-auto shrink-0" aria-label="Мобильное меню">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-xl font-['Cormorant_SC'] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors py-3 px-6 min-h-[44px] flex items-center"
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-8 flex flex-col items-center gap-6 w-full px-8">
            <a
              href="tel:+78129911090"
              className="text-sm tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
              onClick={() =>
                typeof (window as any).ym === "function" &&
                (window as any).ym(109746755, "reachGoal", "phone_click")
              }
            >
              +7 (812) 991-10-90
            </a>
            <button
              className="bg-transparent border border-primary/45 text-accent px-8 py-4 text-[10px] tracking-[0.3em] uppercase w-full max-w-xs hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={() => {
                setMobileMenuOpen(false);
                openBookingModal();
              }}
            >
              Забронировать
            </button>
          </div>
        </nav>
      </div>

      {/*
        Header — z-[65]: above the mobile overlay so the toggle button (X/☰)
        and logo are always tappable.
      */}
      <header
        className={`fixed top-0 left-0 right-0 z-[65] transition-all duration-300 ${
          isScrolled || mobileMenuOpen
            ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm py-3 md:py-4"
            : "bg-transparent py-5 md:py-8"
        }`}
        style={{
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.8s ease 0.2s, background-color 0.3s ease",
          top: "var(--ny-bar-h, 0px)",
          paddingTop: `calc(${nyBar ? "0px" : "env(safe-area-inset-top, 0px)"} + ${isScrolled || mobileMenuOpen ? "0.75rem" : "1.25rem"})`,
        }}
      >
        <div className="container mx-auto px-5 md:px-14 flex items-center justify-between">
          <Link href="/" className="flex items-center group">
            <img
              src="/logo-nav.svg"
              alt="Паруса на крыше"
              className="h-14 md:h-20 w-auto transition-opacity duration-300 group-hover:opacity-75"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  location === link.href
                    ? "text-accent"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              className="bg-transparent border border-primary/45 text-accent px-6 py-2.5 text-[9px] tracking-[0.3em] uppercase cursor-pointer backdrop-blur-sm hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
              onClick={openBookingModal}
            >
              Забронировать
            </button>
          </div>

          {/* Mobile: phone + hamburger */}
          <div className="lg:hidden flex items-center gap-1">
            <a
              href="tel:+78129911090"
              aria-label="Позвонить"
              className="text-accent p-3 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center hover:text-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.9a16 16 0 0 0 6 6l1.27-.82a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z"/>
              </svg>
            </a>
            <button
              className="text-primary p-3 -mr-3 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <BookingModal />
      <ExitIntentModal />
      <main className="flex-grow flex flex-col relative z-10 pb-[72px] lg:pb-0" style={{ paddingTop: "var(--ny-bar-h, 0px)" }}>{children}</main>


      {/* ── Mobile sticky bottom CTA bar ─────────────────────────── */}
      {!mobileMenuOpen && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[190] lg:hidden bg-background/95 backdrop-blur-md border-t border-primary/20 flex"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <a
            href="tel:+78129911090"
            className="flex-1 flex items-center justify-center gap-2 py-4 text-[10px] tracking-[0.2em] uppercase text-accent border-r border-primary/20 hover:bg-primary/10 transition-colors touch-manipulation"
            onClick={() =>
              typeof (window as any).ym === "function" &&
              (window as any).ym(109746755, "reachGoal", "phone_click_sticky")
            }
          >
            <Phone className="w-4 h-4" />
            Позвонить
          </a>
          <button
            onClick={openBookingModal}
            className="flex-1 flex items-center justify-center gap-2 py-4 text-[10px] tracking-[0.2em] uppercase bg-primary text-background hover:bg-accent transition-colors touch-manipulation font-medium"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Забронировать
          </button>
        </div>
      )}

      <footer className="bg-background border-t border-primary/15 pt-20 pb-10 relative z-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2.5rem)" }}>
        <div className="container mx-auto px-6 md:px-14">

          {/* Top: logo + CTA */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 pb-16 border-b border-primary/10">
            <div className="max-w-xs">
              <Link href="/" className="inline-flex items-center gap-3 mb-5 group">
                <div className="w-7 h-[1px] bg-primary group-hover:w-12 transition-all duration-300" />
                <span className="font-['Cormorant_SC'] text-sm tracking-[0.3em] text-primary uppercase">
                  Паруса на крыше
                </span>
              </Link>
              <p className="text-muted-foreground text-sm font-light leading-relaxed">
                Ресторан на крыше в центре Санкт-Петербурга. Панорамный вид 300°, живая музыка, авторская кухня.
              </p>
            </div>
            <button
              onClick={openBookingModal}
              className="self-start md:self-auto border border-primary/40 text-accent px-8 py-4 text-[10px] tracking-[0.35em] uppercase hover:bg-primary hover:text-background hover:border-primary transition-all whitespace-nowrap"
            >
              Забронировать стол
            </button>
          </div>

          {/* Links grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 lg:gap-10 mb-16">

            {/* Контакты */}
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Контакты</h4>
              <ul className="space-y-4 text-muted-foreground font-light text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-3.5 h-3.5 text-primary/60 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed text-muted-foreground">
                    Санкт-Петербург,<br />
                    <MapPicker label="ул. Марата, 5, 6 этаж" className="hover:text-primary transition-colors" dropUp align="left" />
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-3.5 h-3.5 text-primary/60 shrink-0" />
                  <a
                    href="tel:+78129911090"
                    className="hover:text-primary transition-colors text-xs tracking-wider"
                    onClick={() =>
                      typeof (window as any).ym === "function" &&
                      (window as any).ym(109746755, "reachGoal", "phone_click")
                    }
                  >
                    +7 (812) 991-10-90
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-3.5 h-3.5 text-primary/60 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed space-y-1">
                    <p>Пн–Чт, Вс: 12–23</p>
                    <p>Пт–Сб: 12–01</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Ресторан */}
            <div>
              <h4 className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Ресторан</h4>
              <ul className="space-y-3">
                {[
                  { href: "/menu", label: "Меню" },
                  { href: "/interior", label: "Интерьер" },
                  { href: "/afisha", label: "Афиша" },
                  { href: "/contacts", label: "Контакты" },
                  { href: "/vakansii", label: "Вакансии" },
                ].map((l) => (
                  <li key={l.href}>
                    <Link
                      href={l.href}
                      className="text-[10px] tracking-[0.15em] uppercase text-white/55 hover:text-accent transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Мероприятия */}
            <div>
              <h4 className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Мероприятия</h4>
              <ul className="space-y-3">
                {[
                  { href: "/banket", label: "Банкеты" },
                  { href: "/den-rozhdeniya", label: "День рождения" },
                  { href: "/svadba", label: "Свадьба" },
                  { href: "/korporativ", label: "Корпоратив" },
                  { href: "/romanticheskiy-uzhin", label: "Романтика" },
                  { href: "/sertifikat", label: "Сертификаты" },
                ].map((l) => (
                  <li key={l.href}>
                    <Link
                      href={l.href}
                      className="text-[10px] tracking-[0.15em] uppercase text-white/55 hover:text-accent transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Журнал */}
            <div>
              <h4 className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Журнал</h4>
              <ul className="space-y-3">
                {[
                  { href: "/blog", label: "Все статьи" },
                  { href: "/blog/gde-otmetit-den-rozhdeniya-v-peterburge", label: "День рождения" },
                  { href: "/blog/restorany-s-vidom-na-peterburg", label: "Виды на город" },
                  { href: "/blog/romanticheskoe-svidanie-v-spb", label: "Свидание" },
                  { href: "/blog/korporativ-v-peterburge-gde-provesti", label: "Корпоратив" },
                  { href: "/otzyvy", label: "Отзывы гостей" },
                  { href: "/pressa", label: "Пресса и награды" },
                  { href: "/faq", label: "Вопросы и ответы" },
                ].map((l) => (
                  <li key={l.href}>
                    <Link
                      href={l.href}
                      className="text-[10px] tracking-[0.15em] uppercase text-white/55 hover:text-accent transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Соцсети */}
            <div>
              <h4 className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-6">Соцсети</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://vk.com/parusa_nakryshe"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/70 hover:text-accent transition-colors"
                  >
                    ВКонтакте
                  </a>
                </li>

                <li>
                  <a
                    href={REVIEW_STATS.yandex.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/70 hover:text-accent transition-colors"
                  >
                    Яндекс Карты
                  </a>
                </li>
                <li>
                  <a
                    href={REVIEW_STATS.twoGis.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground/70 hover:text-accent transition-colors"
                  >
                    2ГИС
                  </a>
                </li>
              </ul>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="border-t border-primary/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-[10px] tracking-[0.05em] text-muted-foreground/40 normal-case md:whitespace-nowrap">
              ООО «ДОБРО» · ИНН 7813650216 · КПП 781301001 · 197022, Санкт-Петербург, ул. Льва Толстого, 9, лит. А
            </p>
            <div className="flex items-center gap-6 text-[10px] tracking-[0.1em] text-muted-foreground/40 uppercase">
              <span>© 2009–2026</span>
              <Link href="/privacy" className="hover:text-primary transition-colors [text-decoration:none]">
                Конфиденциальность
              </Link>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
}
